import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { IconGraphy, Typography, colors, Input, Button } from '@leapeasy/ui-kit';
import Toolbar from './components/Toolbar';
import EmojiPicker from './components/EmojiPicker';

export const ComposeEmailForm = ({
  title = 'New Email',
  fromEmail = '',
  toEmail = '',
  subject = '',
  initialContent = '',
  onToEmailChange,
  onSubjectChange,
  onClose,
  onSend,
  sendDisabled = false,
  loading = false,
  sendLabel = 'Send',
  sendingLabel = 'Sending...',
  discardLabel = 'Discard',
  fromLabel = 'From Email',
  toLabel = 'To Email',
  subjectLabel = 'Subject',
  showFromEmail = true,
  disableFromEmail = true,
  showToEmailIcon = true,
  showSubjectCopy = false,
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeStyles, setActiveStyles] = useState(new Set());
  const [content, setContent] = useState(initialContent);
  const editorRef = useRef(null);

  useEffect(() => {
    setContent(initialContent || '');
    if (editorRef.current) {
      editorRef.current.innerHTML = initialContent || '';
    }
  }, [initialContent]);

  const updateActiveStyles = () => {
    const styles = new Set();
    if (document.queryCommandState('bold')) styles.add('bold');
    if (document.queryCommandState('italic')) styles.add('italic');
    if (document.queryCommandState('underline')) styles.add('underline');
    if (document.queryCommandState('justifyLeft')) styles.add('justifyLeft');
    if (document.queryCommandState('justifyCenter')) styles.add('justifyCenter');
    if (document.queryCommandState('justifyRight')) styles.add('justifyRight');
    if (document.queryCommandState('justifyFull')) styles.add('justifyFull');
    if (document.queryCommandState('insertUnorderedList')) styles.add('insertUnorderedList');
    if (document.queryCommandState('insertOrderedList')) styles.add('insertOrderedList');
    setActiveStyles(styles);
  };

  const executeCommand = (command, value) => {
    document.execCommand(command, false, value);
    updateActiveStyles();
  };

  const resetNewLineFormatting = () => {
    document.execCommand('justifyLeft', false, null);
    document.execCommand('fontSize', false, '3');
    if (document.queryCommandState('bold')) document.execCommand('bold', false, null);
    if (document.queryCommandState('italic')) document.execCommand('italic', false, null);
    if (document.queryCommandState('underline')) document.execCommand('underline', false, null);
    if (document.queryCommandState('insertOrderedList'))
      document.execCommand('insertOrderedList', false, null);
    if (document.queryCommandState('insertUnorderedList'))
      document.execCommand('insertUnorderedList', false, null);
    updateActiveStyles();
  };

  const handleEditorKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    setTimeout(() => {
      resetNewLineFormatting();
    }, 0);
  };

  const handleEmojiSelect = (emoji) => {
    executeCommand('insertText', emoji);
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
    }
    setShowEmojiPicker(false);
  };

  const handleSend = () => {
    const latestContent = editorRef.current ? editorRef.current.innerHTML : content;
    onSend(latestContent);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '12px',
          borderBottom: '1px solid #E5E5E5',
          marginBottom: '20px',
        }}
      >
        <Typography variant="h4" style={{ color: colors.purple[900], fontWeight: 600 }}>
          {title}
        </Typography>
        <IconGraphy icon="General.Clear" onClick={onClose} />
      </Box>

      {showFromEmail && (
        <Input
          label={fromLabel}
          value={fromEmail}
          type="text"
          minWidth="100%"
          fullWidth
          size="medium"
          disabled={disableFromEmail}
          icon={<IconGraphy icon="Notifications.Email" />}
          showCopy={false}
        />
      )}

      <Input
        label={toLabel}
        value={toEmail}
        onChange={(e) => onToEmailChange(e.target.value)}
        type="text"
        minWidth="100%"
        fullWidth
        size="medium"
        icon={showToEmailIcon ? <IconGraphy icon="Notifications.Email" /> : undefined}
        showCopy={false}
      />

      <Input
        label={subjectLabel}
        value={subject}
        onChange={(e) => onSubjectChange(e.target.value)}
        type="text"
        minWidth="100%"
        fullWidth
        size="medium"
        showCopy={showSubjectCopy}
      />

      <Box sx={{ position: 'relative', marginTop: '8px' }}>
        <Toolbar
          activeStyles={activeStyles}
          onCommand={executeCommand}
          onEmojiClick={() => setShowEmojiPicker((prev) => !prev)}
          showEmojiPicker={showEmojiPicker}
          compact
        />
        {showEmojiPicker && <EmojiPicker onEmojiSelect={handleEmojiSelect} />}
      </Box>

      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => setContent(e.currentTarget.innerHTML)}
        onKeyDown={handleEditorKeyDown}
        onKeyUp={updateActiveStyles}
        onMouseUp={updateActiveStyles}
        sx={{
          minHeight: '180px',
          border: '1px solid rgba(0, 0, 0, 0.35)',
          borderRadius: '8px',
          padding: '12px',
          marginTop: '8px',
          overflow: 'auto',
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, marginTop: '20px' }}>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          {discardLabel}
        </Button>
        <Button variant="primary" onClick={handleSend} disabled={sendDisabled || loading}>
          {loading ? sendingLabel : sendLabel}
        </Button>
      </Box>
    </Box>
  );
};
